'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Bluetooth, CircleSlash, CircuitBoard, Clock, Info, KeyRound, Plug, PlugZap, Unlink, X } from 'lucide-react';
import { type DetectedBoard, type PairedBoard, useBoardEditorStore } from '@/stores/useBoardEditorStore';
import { useModalStore } from '@/stores/useModalStore';
import { useToastStore } from '@/stores/useToastStore';
import ButtonGAdaptive from '@/components/ButtonGAdaptive';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import Header from '@/components/modal/Header';
import { blockSound } from '@/(routers)/boards/editor/block/blockSound';
import ConfirmButton from '@/(routers)/boards/editor/components/modal/ConfirmButton';
import RenderBoardPairingView from '@/(routers)/boards/editor/components/modal/RenderBoardPairingView';

const MOCK_DETECTED_BOARDS: DetectedBoard[] = [
    { type: 'blue', port: 'COM4', is_bluetooth: false },
    { type: 'blue', port: 'COM4', is_bluetooth: true, bluetooth_name: 'SchooMy_BT_EEFF' },
    { type: 'blue', port: 'COM5', is_bluetooth: true, bluetooth_name: 'SchooMy_BT_AABB' },
];

const RING_INNER_SHADOW =
    '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08), inset 0 1px 2px rgba(255,255,255,0.9)';

const MOCK_BOARDS: PairedBoard[] = [
    {
        id: '1',
        boardId: 'SB-00123',
        boardName: 'ブルーボード 1',
        btName: 'SchooMy_BT_EEFF',
        pairedAt: '2026/06/24',
        lastUsedAt: '2026/06/25 14:32',
        chipId: 'COM4',
    },
    {
        id: '2',
        boardId: 'SB-00456',
        boardName: 'ブルーボード 2',
        btName: 'SchooMy_BT_AABB',
        pairedAt: '2026/06/24',
        lastUsedAt: '2026/06/24 09:05',
        chipId: 'COM5',
    },
    {
        id: '3',
        boardId: 'SB-00789',
        boardName: 'ブルーボード 3',
        btName: 'SchooMy_BT_CCDD',
        pairedAt: '2026/06/25',
        lastUsedAt: '2026/06/25 21:18',
        chipId: 'COM6',
    },
];

type License = {
    id: string;
    key: string;
    plan: 'API' | 'PLUS';
};

const PLAN_COLOR: Record<string, string> = { API: '#2876fd', PLUS: '#04e657' };

const MOCK_LICENSES: License[] = [];

type FeatureNode = {
    num: string;
    label: string;
    value: string;
    icon: React.ReactNode;
    active: boolean;
    valueFont?: string;
    valueNode?: React.ReactNode;
};

type ConnectionKind = 'usb' | 'bluetooth';

const ALL_KINDS: ConnectionKind[] = ['usb', 'bluetooth'];

const KIND_STYLE: Record<ConnectionKind, { color: string; rotation: number }> = {
    usb: { color: '#04e657', rotation: 90 },
    bluetooth: { color: '#2876fd', rotation: 270 },
};

const RING_SEGMENT_DURATION = 0.8;

function BoardRing({
    connections,
    isBridgeConnected,
}: {
    connections: Set<ConnectionKind>;
    isBridgeConnected: boolean;
}) {
    const cx = 100;
    const r = 84;
    const strokeW = 20;
    const C = 2 * Math.PI * r;
    const segLen = C / 2;

    return (
        <div className="flex flex-col items-center gap-2">
            <div className="relative flex-shrink-0 w-[240px] h-[240px]">
                <svg
                    className="absolute inset-0 w-full h-full transition-all duration-500"
                    viewBox="0 0 200 200"
                    style={{ overflow: 'visible' }}
                >
                    {isBridgeConnected ? (
                        <>
                            {ALL_KINDS.map((kind, i) => {
                                const active = connections.has(kind);
                                const s = KIND_STYLE[kind];
                                return (
                                    <motion.circle
                                        key={kind}
                                        cx={cx}
                                        cy={cx}
                                        r={r}
                                        fill="none"
                                        stroke={active ? s.color : '#d1d5db'}
                                        strokeWidth={strokeW}
                                        strokeLinecap="butt"
                                        initial={{ strokeDasharray: `0 ${C}` }}
                                        animate={{ strokeDasharray: `${segLen} ${C - segLen}` }}
                                        transition={{
                                            duration: RING_SEGMENT_DURATION,
                                            delay: i * RING_SEGMENT_DURATION,
                                            ease: 'easeOut',
                                        }}
                                        transform={`rotate(${s.rotation}, ${cx}, ${cx})`}
                                        style={{ transition: 'stroke 0.4s' }}
                                    />
                                );
                            })}
                            {/* gap masks at 12 and 6 o'clock */}
                            <line
                                x1={cx}
                                y1={cx + r - strokeW / 2 - 1}
                                x2={cx}
                                y2={cx + r + strokeW / 2 + 1}
                                stroke="white"
                                strokeWidth={12}
                                strokeLinecap="butt"
                            />
                            <line
                                x1={cx}
                                y1={cx - r - strokeW / 2 - 1}
                                x2={cx}
                                y2={cx - r + strokeW / 2 + 1}
                                stroke="white"
                                strokeWidth={12}
                                strokeLinecap="butt"
                            />
                        </>
                    ) : (
                        // ブリッジ無し(Web Serial API)ではBluetoothという概念自体が存在しないため、
                        // 分割せずUSBのみの単色フルリングにする
                        <motion.circle
                            cx={cx}
                            cy={cx}
                            r={r}
                            fill="none"
                            stroke={connections.has('usb') ? KIND_STYLE.usb.color : '#d1d5db'}
                            strokeWidth={strokeW}
                            strokeLinecap="butt"
                            initial={{ strokeDasharray: `0 ${C}` }}
                            animate={{ strokeDasharray: `${C} 0` }}
                            transition={{ duration: RING_SEGMENT_DURATION, ease: 'easeOut' }}
                            style={{ transition: 'stroke 0.4s' }}
                        />
                    )}
                </svg>
                <div
                    className="absolute inset-[40px] rounded-full bg-white flex flex-col items-center justify-center px-2"
                    style={{ boxShadow: RING_INNER_SHADOW }}
                >
                    <ImageAdaptive src="/images/block/board_blue.svg" baseWidth={68} breakpoints={{ md: 108 }} />
                </div>
            </div>
        </div>
    );
}

function BoardCard({
    board,
    index,
    onUnpair,
    assignedLicense,
    onDropLicense,
    onRemoveLicense,
}: {
    board: PairedBoard;
    index: number;
    onUnpair: (id: string) => void;
    assignedLicense?: License;
    onDropLicense: (licenseId: string) => void;
    onRemoveLicense: () => void;
}) {
    const [deleting, setDeleting] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const dragCounter = useRef(0);
    const justRemoved = useRef(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const iconRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [dotYs, setDotYs] = useState<number[]>([19.5, 34.8, 50, 65.2, 80.5]);
    const isBridgeConnected = useBoardEditorStore((s) => s.isBridgeConnected);
    const detectedBoards = MOCK_DETECTED_BOARDS;
    const connections = new Set<ConnectionKind>([
        ...(detectedBoards.some((d) => d.port === board.chipId && !d.is_bluetooth)
            ? (['usb'] as ConnectionKind[])
            : []),
        ...(detectedBoards.some((d) => d.is_bluetooth && d.bluetooth_name === board.btName)
            ? (['bluetooth'] as ConnectionKind[])
            : []),
    ]);

    const STATUS_SHORT: Record<ConnectionKind, string> = { usb: 'USB', bluetooth: 'Bluetooth' };
    const statusNode =
        connections.size === 0 ? (
            <span
                className="inline-flex items-center px-4 rounded-full text-white font-montserrat font-bold text-[11px] tracking-wide"
                style={{ backgroundColor: '#d1d5db' }}
            >
                未接続
            </span>
        ) : (
            <span className="flex items-center gap-1.5 flex-wrap">
                {[...connections].map((k) => (
                    <span
                        key={k}
                        className="inline-flex items-center px-4 rounded-full text-white font-montserrat font-bold text-[11px] tracking-wide"
                        style={{ backgroundColor: KIND_STYLE[k].color }}
                    >
                        {STATUS_SHORT[k]}
                    </span>
                ))}
                <span className="font-montserrat font-bold text-[11px] text-color-base">に接続中</span>
            </span>
        );

    const features: FeatureNode[] = [
        {
            num: '01',
            label: 'ボード名',
            value: board.boardId,
            icon: <CircuitBoard size={18} />,
            active: true,
        },
        {
            num: '02',
            label: '最終書き込み日時',
            value: board.lastUsedAt,
            icon: <Clock size={18} />,
            active: true,
            valueFont: 'font-cantarell',
        },
        {
            num: '03',
            label: '接続ステータス',
            value: connections.size === 0 ? '未接続' : [...connections].map((k) => STATUS_SHORT[k]).join(' / '),
            valueNode: statusNode,
            icon: <Plug size={18} />,
            active: true,
        },
        {
            num: '04',
            label: 'Bluetooth',
            value: board.btName,
            icon: <Bluetooth size={18} />,
            active: true,
        },
        {
            num: '05',
            label: 'ライセンス',
            value: assignedLicense?.key ?? '',
            icon: <KeyRound size={18} />,
            active: true,
            valueNode: (
                <div
                    draggable={!!assignedLicense}
                    onDragStart={(e) => {
                        if (assignedLicense) {
                            e.dataTransfer.setData('licenseId', assignedLicense.id);
                            e.dataTransfer.setData('sourceBoardId', board.id);
                            justRemoved.current = false;
                        }
                    }}
                    onDragLeave={() => {
                        if (assignedLicense) {
                            justRemoved.current = true;
                        }
                    }}
                    onDragEnd={(e) => {
                        if (justRemoved.current && e.dataTransfer.dropEffect === 'none') {
                            justRemoved.current = false;
                            onRemoveLicense();
                        }
                    }}
                    className={`flex items-center gap-3 pl-2 pr-3 py-2 rounded-full text-sm font-cantarell font-bold transition-all ${
                        assignedLicense ? 'cursor-grab active:cursor-grabbing drop-shadow-md' : ''
                    }`}
                    style={
                        assignedLicense
                            ? { border: '2px solid transparent', backgroundColor: PLAN_COLOR[assignedLicense.plan] }
                            : isDragOver
                            ? { border: '2px dashed #2876fd', backgroundColor: '#eff6ff' }
                            : { border: '2px dashed #d1d5db', backgroundColor: '#f8fafc' }
                    }
                >
                    <span
                        className={`w-9 h-9 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${
                            assignedLicense ? 'text-[10px]' : 'text-lg'
                        }`}
                        style={
                            assignedLicense
                                ? { backgroundColor: 'white', color: PLAN_COLOR[assignedLicense.plan] }
                                : {
                                      backgroundColor: isDragOver ? '#bfdbfe' : '#e5e7eb',
                                      color: isDragOver ? 'white' : '#9ca3af',
                                  }
                        }
                    >
                        {assignedLicense ? assignedLicense.plan : '+'}
                    </span>
                    <span
                        className={`${
                            assignedLicense ? 'text-white' : isDragOver ? 'text-blue-500' : 'text-gray-400'
                        } ${!assignedLicense && !isDragOver ? 'text-xs' : ''}`}
                    >
                        {assignedLicense ? assignedLicense.key : isDragOver ? 'ここにドロップ' : 'ライセンス未割当'}
                    </span>
                    {assignedLicense && (
                        <button
                            onClick={onRemoveLicense}
                            className="w-6 h-6 p-0 rounded-full bg-black/20 hover:bg-black/30 flex items-center justify-center flex-shrink-0 transition-colors leading-none"
                        >
                            <X size={13} className="text-white" />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    useLayoutEffect(() => {
        const card = cardRef.current;
        if (!card) return;
        const cardRect = card.getBoundingClientRect();
        if (cardRect.height === 0) return;
        const ys = iconRefs.current.map((el) => {
            if (!el) return 50;
            const r = el.getBoundingClientRect();
            return ((r.top + r.height / 2 - cardRect.top) / cardRect.height) * 100;
        });
        if (ys.length === features.length && ys.every((y) => Number.isFinite(y))) {
            setDotYs(ys);
        }
    }, [features.length, assignedLicense, connections.size, isDragOver]);

    return (
        <motion.div
            layout="position"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="flex flex-col mt-4"
            onDragEnter={(e) => {
                e.preventDefault();
                dragCounter.current++;
                setIsDragOver(true);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => {
                e.preventDefault();
                dragCounter.current = Math.max(0, dragCounter.current - 1);
                if (dragCounter.current === 0) setIsDragOver(false);
            }}
            onDrop={(e) => {
                e.preventDefault();
                dragCounter.current = 0;
                setIsDragOver(false);
                const licenseId = e.dataTransfer.getData('licenseId');
                const sourceBoardId = e.dataTransfer.getData('sourceBoardId');
                // 同じカード上への再ドロップ(少しドラッグして離した場合等)は、解除ではなく
                // 何もしない扱いにする。onDragLeave経由でjustRemovedが立った状態のまま
                // 同一カードへ戻すと、外したつもりが無いのにライセンスが解除される不具合があった。
                if (sourceBoardId === board.id) {
                    justRemoved.current = false;
                    return;
                }
                if (justRemoved.current) {
                    justRemoved.current = false;
                    onRemoveLicense();
                    return;
                }
                if (licenseId) onDropLicense(licenseId);
            }}
        >
            <div
                ref={cardRef}
                className="relative bg-white rounded-2xl h-[380px] transition-all duration-200"
                style={{ border: isDragOver ? '1.5px solid #2876fd' : '1.5px solid rgba(0, 0, 0, 0.10)' }}
            >
                <div className="absolute -top-[14px] left-3 rounded-md w-20 py-0.5 flex items-center justify-center bg-color-base">
                    <span className="font-cantarell font-bold text-xs text-white">
                        {String(index).padStart(2, '0')}
                    </span>
                </div>

                <div className="absolute right-3 top-4 z-20">
                    <ButtonGAdaptive
                        label="ペアリング解除"
                        icon={Unlink}
                        size="xs"
                        colorType={1}
                        onClick={() => setDeleting(true)}
                    />
                </div>
                <svg
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                >
                    {(() => {
                        const y0 = dotYs[0] ?? 19.5;
                        const y1 = dotYs[dotYs.length - 1] ?? 80.5;
                        const midIndex = Math.floor(dotYs.length / 2);
                        const yMid = dotYs[midIndex] ?? 50;
                        const arcX = (y: number) => {
                            const t = y1 === y0 ? 0 : (y - y0) / (y1 - y0);
                            return 39 - 20 * t + 20 * t * t;
                        };
                        return (
                            <>
                                <path
                                    d={`M 39,${y0} Q 29,${yMid} 39,${y1}`}
                                    fill="none"
                                    stroke="#d1d5db"
                                    strokeWidth="2"
                                    vectorEffect="non-scaling-stroke"
                                />
                                {dotYs.map((y, i) => (
                                    <line
                                        key={i}
                                        x1={i === midIndex ? 27 : arcX(y)}
                                        y1={y}
                                        x2="46"
                                        y2={y}
                                        stroke="#d1d5db"
                                        strokeWidth="1.2"
                                        vectorEffect="non-scaling-stroke"
                                    />
                                ))}
                            </>
                        );
                    })()}
                </svg>

                <div
                    className="absolute left-[2%] -translate-y-1/2"
                    style={{ top: `${dotYs[Math.floor(dotYs.length / 2)] ?? 50}%` }}
                >
                    <BoardRing connections={connections} isBridgeConnected={isBridgeConnected} />
                </div>

                <div className="absolute flex flex-col justify-center gap-6 left-[47%] right-[3%] inset-y-0 pointer-events-none">
                    {features.map((f, i) => (
                        <div
                            key={f.num}
                            className={`flex gap-4 min-h-10 pointer-events-auto ${
                                i === features.length - 1 ? 'items-start mt-2' : 'items-center'
                            } ${!f.active ? 'opacity-80' : ''}`}
                        >
                            <div
                                ref={(el) => {
                                    iconRefs.current[i] = el;
                                }}
                                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                                    f.active ? 'bg-color-base text-white shadow-md' : 'bg-gray-100 text-gray-400 '
                                }`}
                            >
                                {f.icon}
                            </div>
                            <div className="flex flex-col gap-0.5">
                                <div
                                    className={`flex items-center gap-1.5 text-xs ${
                                        f.active ? 'text-gray-400' : 'text-gray-300'
                                    }`}
                                >
                                    <span>{f.label}</span>
                                </div>
                                <div
                                    className={`${f.valueFont ?? 'font-montserrat'} font-bold text-sm ${
                                        f.active ? 'text-color-base' : 'text-gray-300'
                                    }`}
                                >
                                    {f.valueNode ?? f.value}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {deleting && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="absolute inset-0 z-30 rounded-2xl bg-black/10 flex items-center justify-center px-5"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.15 }}
                            className="w-full max-w-[350px] bg-white rounded-xl shadow-lg border border-gray-200 px-5 py-4 flex flex-col items-center gap-1"
                        >
                            <div className="flex justify-center items-center gap-2">
                                <IconAdaptive Icon={Info} baseSize={16} className="text-color-ide-file-discode" />
                                <div className="font-cantarell text-xs text-color-ide-file-discode text-center">
                                    ペアリングを解除しますか？
                                </div>
                            </div>
                            <div className="flex justify-center items-center gap-2">
                                <div className="font-cantarell text-xs text-gray-600">{board.boardId} を解除します</div>
                            </div>
                            <div className="flex justify-center items-center gap-2 mt-3">
                                <ConfirmButton onClick={() => setDeleting(false)} variant="cancel">
                                    キャンセル
                                </ConfirmButton>
                                <ConfirmButton onClick={() => onUnpair(board.id)} variant="danger">
                                    解除
                                </ConfirmButton>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </div>
        </motion.div>
    );
}

function OwnedLicensesSection({
    licenses,
    boardLicenses,
}: {
    licenses: License[];
    boardLicenses: Record<string, string>;
}) {
    const assignedIds = new Set(Object.values(boardLicenses));

    return (
        <div className="relative mt-5 pb-3">
            <div className="absolute -top-[14px] left-3 rounded-md px-3 py-0.5 flex items-center justify-center bg-color-base">
                <span className="font-cantarell font-bold text-xs text-white">所有ライセンス</span>
            </div>
            <div className="rounded-2xl px-5 py-5" style={{ border: '1.5px solid rgba(0,0,0,0.10)' }}>
                {licenses.length === 0 ? (
                    <div className="flex items-center justify-center gap-2 py-4 text-color-co-gray-dark">
                        <IconAdaptive Icon={CircleSlash} baseSize={24} className="text-color-co-gray-dark" />
                        <span className="text-sm font-notojp">所有するライセンスがありません</span>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-wrap gap-2">
                            {licenses.map((lic) => {
                                const assigned = assignedIds.has(lic.id);
                                return (
                                    <div
                                        key={lic.id}
                                        draggable={!assigned}
                                        onDragStart={(e) => e.dataTransfer.setData('licenseId', lic.id)}
                                        className={`flex items-center gap-3 pl-2 pr-5 py-2 rounded-full text-sm font-cantarell font-bold select-none transition-all ${
                                            assigned
                                                ? 'text-gray-300 cursor-not-allowed'
                                                : 'text-white cursor-grab active:cursor-grabbing drop-shadow-md hover:drop-shadow-lg'
                                        }`}
                                        style={
                                            assigned
                                                ? { backgroundColor: '#f3f4f6' }
                                                : { backgroundColor: PLAN_COLOR[lic.plan] }
                                        }
                                    >
                                        <span
                                            className="w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                                            style={
                                                assigned
                                                    ? { backgroundColor: '#d1d5db', color: 'white' }
                                                    : { backgroundColor: 'white', color: PLAN_COLOR[lic.plan] }
                                            }
                                        >
                                            {lic.plan}
                                        </span>
                                        {lic.key}
                                        {assigned && (
                                            <span className="text-[10px] font-corp text-gray-300">割当済</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-400 font-notojp mt-3">
                            <IconAdaptive
                                Icon={Info}
                                baseSize={16}
                                breakpoints={{ md: 16 }}
                                className="flex-shrink-0"
                            />
                            <span>ライセンスをドラッグしてボードに装着してください</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default function BoardMyBoards({ onClose: _onClose }: { onClose?: () => void }) {
    const [boards, setBoards] = useState<PairedBoard[]>([]);
    const [licenses] = useState<License[]>(MOCK_LICENSES);
    const [boardLicenses, setBoardLicenses] = useState<Record<string, string>>({});
    const { openModal } = useModalStore();
    const showToastAsync = useToastStore((s) => s.showToastAsync);
    const setPairedBoards = useBoardEditorStore((s) => s.setPairedBoards);

    const fetchBoards = async () => {
        setBoards(MOCK_BOARDS);
        // ツールバーでボード名(btName)を表示できるよう、グローバルストアにも反映しておく
        setPairedBoards(MOCK_BOARDS);
    };
    useEffect(() => {
        fetchBoards();
    }, []);

    const handleUnpair = async (id: string) => {
        const next = boards.filter((b) => b.id !== id);
        setBoards(next);
        setPairedBoards(next);
        setBoardLicenses((prev) => {
            const n = { ...prev };
            delete n[id];
            return n;
        });
        await showToastAsync('ペアリングを解除しました', 'success', 1000);
    };

    const handleDropLicense = (boardId: string, licenseId: string) => {
        setBoardLicenses((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((k) => {
                if (next[k] === licenseId) delete next[k];
            });
            next[boardId] = licenseId;
            return next;
        });
        blockSound.play('connect');
    };

    const handleOpenPairing = () => {
        openModal({
            id: 'board-pairing-modal',
            component: <RenderBoardPairingView pairedBoards={boards} onPaired={fetchBoards} />,
            size: { pcWidth: '500px', mbWidth: '90%', pcHeight: '420px', mbHeight: '80%' },
            direction: 'clip',
            zIndex: 2500,
        });
    };

    return (
        <div className="w-full h-full flex flex-col p-2">
            <div className="flex-shrink-0 flex flex-row w-full h-12 px-2">
                <div className="flex justify-end w-3/4">
                    <Header title="MY BOARD" subTitle="" icon={CircuitBoard} />
                </div>
            </div>

            <div className="flex-shrink-0 px-2 pt-1 pb-2 flex flex-col gap-4">
                <div className="flex justify-center">
                    <ButtonGAdaptive
                        label="スクーミーブルーボードをペアリングする"
                        description="pair SchooMy blue board"
                        icon={PlugZap}
                        size="xl"
                        onClick={handleOpenPairing}
                    />
                </div>
                <OwnedLicensesSection licenses={licenses} boardLicenses={boardLicenses} />
            </div>

            <div
                className="flex-1 overflow-y-auto px-2 pb-4 flex flex-col gap-10"
                style={{ scrollbarGutter: 'stable' }}
            >
                {boards.length === 0 && (
                    <div className="flex items-center justify-center py-12 gap-2 text-gray-400">
                        <IconAdaptive
                            Icon={Info}
                            strokeWidth={2.0}
                            baseSize={18}
                            breakpoints={{ md: 20 }}
                            className="mt-[1px]"
                        />
                        <span className="text-sm font-cantarell">ペアリング済みのボードがありません</span>
                    </div>
                )}
                {boards.map((board, i) => (
                    <BoardCard
                        key={board.id}
                        board={board}
                        index={i + 1}
                        onUnpair={handleUnpair}
                        assignedLicense={licenses.find((l) => l.id === boardLicenses[board.id])}
                        onDropLicense={(licenseId) => handleDropLicense(board.id, licenseId)}
                        onRemoveLicense={() => {
                            setBoardLicenses((prev) => {
                                const n = { ...prev };
                                delete n[board.id];
                                return n;
                            });
                            blockSound.play('delete');
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
